// Mídia e direção criativa da peça: A10 (arte) e A11 (vídeo), geração no Higgsfield, upload (designer, Canva, Artlist)
// e escolha da mídia que entra na versão aprovável.
import { useEffect, useState } from 'react';
import { api, ApiError, fmtDate } from '../api';
import { Badge, Button, Card, Empty, ErrorBox, Field, Flash, inputCls, useAction } from './ui';

export interface MediaFile {
  id: string;
  kind: 'image' | 'video' | 'document';
  mimeType: string | null;
  bytes: number | null;
  status: string;
  source: string;
  prompt: string | null;
  model: string | null;
  originalName: string | null;
  rightsNote: string | null;
  branding: string | null;
  sourceRef?: string | null;
  error: string | null;
  createdAt: string;
}

export function MediaThumb({ file, className = '' }: { file: Pick<MediaFile, 'id' | 'kind'> & { sourceRef?: string | null }; className?: string }) {
  // Carrossel (PDF): mostra a capa e o link para abrir o documento.
  if (file.kind === 'document')
    return (
      <div className="relative">
        {file.sourceRef ? <img src={`/api/media/${file.sourceRef}`} alt="Capa do carrossel" className={className} loading="lazy" /> : <div className={`${className} flex items-center justify-center`}>PDF</div>}
        <a href={`/api/media/${file.id}`} target="_blank" rel="noreferrer" className="absolute bottom-1 left-1 rounded bg-white/90 px-2 py-0.5 text-xs font-semibold text-brand-900 underline">Abrir PDF</a>
      </div>
    );
  return file.kind === 'video' ? (
    <video src={`/api/media/${file.id}`} className={className} controls preload="metadata" />
  ) : (
    <img src={`/api/media/${file.id}`} alt="" className={className} loading="lazy" />
  );
}

const SOURCE_LABEL: Record<string, string> = { upload: 'upload', higgsfield: 'Higgsfield', openai: 'ChatGPT', claude: 'Claude', canva: 'Canva', artlist: 'Artlist' };
const PROVIDER_LABEL: Record<string, string> = { higgsfield: 'Higgsfield', openai: 'ChatGPT (OpenAI)' };

interface AudioSuggestion {
  name: string;
  genre: string;
  bpm: number;
  energy: string;
  mood: string;
  whereInVideo: string;
  artlistSearch: string[];
  why: string;
}

function audioSuggestions(m: MediaFile): AudioSuggestion[] {
  if (m.kind !== 'video' || m.source !== 'claude' || !m.prompt?.startsWith('{')) return [];
  try {
    return (JSON.parse(m.prompt) as { audio?: AudioSuggestion[] }).audio ?? [];
  } catch {
    return [];
  }
}

/** Trilhas sugeridas pelo Claude (A10) + envio da faixa licenciada para mixar no vídeo. */
function VideoAudio({ assetId, file, canEdit, onDone }: { assetId: string; file: MediaFile; canEdit: boolean; onDone: () => void }) {
  const act = useAction();
  const [track, setTrack] = useState<File | null>(null);
  const [license, setLicense] = useState('Artlist, licença comercial');
  const tips = audioSuggestions(file);
  const send = async () => {
    if (!track) return;
    const fd = new FormData();
    fd.append('license', license);
    fd.append('file', track);
    const res = await fetch(`/api/assets/${assetId}/media/${file.id}/audio`, { method: 'POST', body: fd, credentials: 'same-origin', headers: { 'x-requested-with': 'mcc' } });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, data?.error ?? 'ERROR', data?.message ?? `Erro ${res.status}`);
    setTrack(null);
  };
  return (
    <details className="mt-2 rounded border border-slate-200 bg-white p-2">
      <summary className="cursor-pointer font-semibold text-brand-900">Trilha sonora {tips.length ? `(${tips.length} sugestões)` : ''}</summary>
      <ErrorBox error={act.error} />
      <Flash message={act.message} />
      {tips.length > 0 && (
        <ul className="mt-2 space-y-2">
          {tips.map((t) => (
            <li key={t.name} className="rounded bg-slate-50 p-2">
              <p className="font-semibold">{t.name}</p>
              <p className="text-slate-600">
                {t.genre} · {t.bpm} BPM · energia {t.energy} · {t.mood}
              </p>
              <p className="text-slate-600">Onde: {t.whereInVideo}</p>
              <p className="text-slate-600">{t.why}</p>
              <p className="mt-1">
                Buscar na Artlist: <span className="font-mono">{t.artlistSearch.join(', ')}</span> <CopyButton text={t.artlistSearch.join(' ')} label="Copiar busca" />
              </p>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-slate-600">Use só faixas licenciadas para uso comercial (Artlist). Música em alta de artistas é protegida: conta de empresa não pode usar.</p>
      {canEdit && (
        <div className="mt-2 space-y-2">
          <input className="text-sm" type="file" accept="audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,.mp3,.wav,.m4a" onChange={(e) => setTrack(e.target.files?.[0] ?? null)} />
          <input className={inputCls} value={license} onChange={(e) => setLicense(e.target.value)} aria-label="Origem e licença da trilha" />
          <Button variant="secondary" disabled={act.busy || !track || license.trim().length < 5} onClick={() => void act.run(send, 'Trilha mixada: o vídeo com som foi adicionado à biblioteca.').then(onDone)}>
            Colocar trilha no vídeo
          </Button>
        </div>
      )}
    </details>
  );
}

function CopyButton({ text, label = 'Copiar' }: { text: string; label?: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" className="text-xs text-brand-700 underline" onClick={() => void navigator.clipboard?.writeText(text).then(() => setOk(true))}>
      {ok ? 'Copiado ✓' : label}
    </button>
  );
}

export function MediaPanel({
  assetId,
  media,
  attachedIds,
  creative,
  generation,
  canEdit,
  onChange,
}: {
  assetId: string;
  media: MediaFile[];
  attachedIds: string[];
  creative: { A10: { output: any; at: string } | null; A11: { output: any; at: string } | null };
  generation: { provider: string | null; status: string; image: boolean; video: boolean; designVideo?: boolean; providers?: { provider: string; status: string; image: boolean; video: boolean }[] };
  canEdit: boolean;
  onChange: () => void;
}) {
  const act = useAction();
  const [prompt, setPrompt] = useState('');
  const [kind, setKind] = useState<'image' | 'video'>('image');
  const providers = generation.providers?.length ? generation.providers : [{ provider: generation.provider ?? 'higgsfield', status: generation.status, image: generation.image, video: generation.video }];
  // Padrão: ChatGPT para imagem quando conectado; senão o primeiro gerador.
  const [provider, setProvider] = useState(() => (providers.find((p) => p.provider === 'openai' && p.status === 'CONNECTED') ?? providers[0]).provider);
  const sel = providers.find((p) => p.provider === provider) ?? providers[0];
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState('upload');
  const [rights, setRights] = useState('');
  const [hasLogo, setHasLogo] = useState(false);
  const [alt, setAlt] = useState('');
  const generating = media.some((m) => m.status === 'GENERATING');

  // Atualiza sozinho enquanto há geração em andamento.
  useEffect(() => {
    if (!generating) return;
    const t = setInterval(onChange, 5000);
    return () => clearInterval(t);
  }, [generating, onChange]);

  const a10 = creative.A10?.output;
  const a11 = creative.A11?.output;
  useEffect(() => {
    if (!prompt && a10?.imagePrompt) setPrompt(a10.imagePrompt);
    if (!alt && a10?.altText) setAlt(a10.altText);
  }, [a10, prompt, alt]);

  const upload = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('source', source);
    if (rights) fd.append('rightsNote', rights);
    fd.append('logo', hasLogo ? 'ja_tem' : 'aplicar');
    fd.append('file', file);
    const res = await fetch(`/api/assets/${assetId}/media`, { method: 'POST', body: fd, credentials: 'same-origin', headers: { 'x-requested-with': 'mcc' } });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, data?.error ?? 'ERROR', data?.message ?? `Erro ${res.status}`);
    setFile(null);
    setHasLogo(false);
  };

  const label = PROVIDER_LABEL[sel.provider] ?? sel.provider;
  const genReason = sel.status !== 'CONNECTED' ? `${label} sem chave de API configurada.` : kind === 'image' ? (sel.image ? '' : `${label} sem modelo de imagem.`) : sel.video ? '' : sel.provider === 'openai' ? 'O ChatGPT gera só imagem; para vídeo use o Higgsfield.' : 'Nenhum modelo de vídeo configurado (HIGGSFIELD_VIDEO_MODEL).';

  return (
    <>
      <Card title="Direção criativa (A10 arte · A11 vídeo)">
        <ErrorBox error={act.error} />
        <Flash message={act.message} />
        {canEdit && (
          <div className="mb-3 flex flex-wrap gap-2">
            <Button variant="secondary" disabled={act.busy} onClick={() => void act.run(() => api(`/api/assets/${assetId}/creative`, { method: 'POST', body: { agent: 'A10' } }), 'Direção de arte gerada.').then(onChange)}>
              {act.busy ? 'Gerando…' : a10 ? 'Refazer direção de arte (A10)' : 'Gerar direção de arte (A10)'}
            </Button>
            <Button variant="secondary" disabled={act.busy} onClick={() => void act.run(() => api(`/api/assets/${assetId}/creative`, { method: 'POST', body: { agent: 'A11' } }), 'Roteiro de vídeo gerado.').then(onChange)}>
              {a11 ? 'Refazer roteiro de vídeo (A11)' : 'Gerar roteiro de vídeo (A11)'}
            </Button>
          </div>
        )}
        {a10 ? (
          <div className="space-y-2 text-sm">
            <p>
              <strong>Conceito:</strong> {a10.concept}
            </p>
            <p>
              <strong>Arte {a10.format?.width}×{a10.format?.height}:</strong> “{a10.headlineOnImage}” — {a10.layout}
            </p>
            <div className="rounded bg-slate-50 p-2 text-xs">
              <div className="flex justify-between gap-2">
                <strong>Prompt da imagem (Higgsfield/Artlist)</strong>
                <span className="flex gap-3">
                  <CopyButton text={a10.imagePrompt} />
                  <a className="text-brand-700 underline" href="https://artlist.io" target="_blank" rel="noreferrer noopener">
                    Abrir Artlist ↗
                  </a>
                </span>
              </div>
              <p className="mt-1 font-mono">{a10.imagePrompt}</p>
              <p className="mt-1 text-slate-500">Evitar: {a10.negativePrompt}</p>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer font-semibold">Montagem no Canva</summary>
              <ol className="mt-1 list-decimal pl-5">{(a10.canvaInstructions as string[]).map((s) => <li key={s}>{s}</li>)}</ol>
              <a className="text-brand-700 underline" href="https://www.canva.com/" target="_blank" rel="noreferrer noopener">
                Abrir Canva ↗
              </a>
            </details>
            <p className="text-xs text-slate-500">Atualizado {fmtDate(creative.A10!.at)}</p>
          </div>
        ) : (
          <Empty>Sem direção de arte ainda.</Empty>
        )}
        {a11 && (
          <div className="mt-3 border-t border-slate-200 pt-3 text-sm">
            <p>
              <strong>Vídeo {a11.durationS}s · {a11.aspect}</strong> — gancho: “{a11.hook}”
            </p>
            <ol className="mt-1 space-y-1 text-xs">
              {(a11.scenes as any[]).map((s, i) => (
                <li key={i}>
                  <span className="font-mono">
                    {s.start}–{s.end}s
                  </span>{' '}
                  {s.visual} · <em>{s.narration}</em>
                  {s.onScreenText ? ` · tela: “${s.onScreenText}”` : ''}
                </li>
              ))}
            </ol>
            <div className="mt-2 rounded bg-slate-50 p-2 text-xs">
              <div className="flex justify-between">
                <strong>Prompt do vídeo</strong>
                <CopyButton text={a11.videoPrompt} />
              </div>
              <p className="mt-1 font-mono">{a11.videoPrompt}</p>
              <p className="mt-1">Trilha: {a11.musicDirection}</p>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer font-semibold">Legendas</summary>
              <pre className="whitespace-pre-wrap">{a11.captions}</pre>
              <CopyButton text={a11.captions} label="Copiar legendas" />
            </details>
          </div>
        )}
      </Card>

      <Card title="Mídia da peça" actions={generating ? <Badge tone="blue">gerando…</Badge> : undefined}>
        {media.length ? (
          <ul className="grid grid-cols-2 gap-3">
            {media.map((m) => {
              const attached = attachedIds.includes(m.id);
              return (
                <li key={m.id} className={`rounded border p-2 text-xs ${attached ? 'border-emerald-500 ring-1 ring-emerald-500' : 'border-slate-200'}`}>
                  {m.status === 'READY' ? (
                    <MediaThumb file={m} className="h-32 w-full rounded bg-slate-100 object-cover" />
                  ) : (
                    <div className="flex h-32 items-center justify-center rounded bg-slate-100 text-center text-slate-600">
                      {m.status === 'GENERATING' ? 'Gerando no provedor…' : m.status === 'FAILED' ? `Falhou: ${m.error ?? ''}` : m.status}
                    </div>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <Badge>{SOURCE_LABEL[m.source] ?? m.source}</Badge>
                    {attached && <Badge tone="green">na peça</Badge>}
                    {m.status === 'READY' && (m.branding ? <Badge tone="green">{m.branding === 'auto' ? 'logo 3S aplicado' : 'logo 3S (declarado)'}</Badge> : <Badge tone="red">sem logo 3S: não aprova</Badge>)}
                    {m.status === 'FAILED' && <Badge tone="red">falhou</Badge>}
                  </div>
                  {m.prompt && !/^[[{]/.test(m.prompt) && <p className="mt-1 line-clamp-2 text-slate-600" title={m.prompt}>{m.prompt}</p>}
                  {m.rightsNote && <p className="mt-1 text-slate-500">{m.rightsNote}</p>}
                  {m.kind === 'video' && m.status === 'READY' && <VideoAudio assetId={assetId} file={m} canEdit={canEdit} onDone={onChange} />}
                  {canEdit && m.status === 'READY' && (
                    <button
                      className="mt-1 text-brand-700 underline"
                      onClick={() =>
                        void act
                          .run(
                            () => api(`/api/assets/${assetId}/media/attach`, { method: 'POST', body: { fileIds: attached ? [] : [m.id], alt: alt || undefined } }),
                            attached ? 'Mídia removida da peça (nova versão; reaprovação necessária).' : 'Mídia anexada (nova versão; reaprovação necessária).',
                          )
                          .then(onChange)
                      }
                    >
                      {attached ? 'Remover da peça' : 'Usar na peça'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <Empty>Nenhuma imagem, carrossel ou vídeo ainda.</Empty>
        )}
        {canEdit && (
          <>
            <Field label="Texto alternativo (acessibilidade)" hint="Vai junto com a mídia anexada.">
              <input className={inputCls} value={alt} onChange={(e) => setAlt(e.target.value)} />
            </Field>
            <p className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-900">Anexar ou trocar a mídia cria nova versão e exige nova aprovação. Toda arte leva o logo da 3S: em imagens (PNG, JPG, WEBP) o sistema aplica o logo oficial no canto inferior direito automaticamente.</p>

            <h3 className="mt-4 text-xs font-semibold uppercase text-slate-500">Criar com Claude (designer na identidade 3S)</h3>
            <p className="mt-1 text-xs text-slate-600">O Claude decide os slides a partir do texto da peça; o sistema desenha com Montserrat, as cores do manual e o logo. Carrossel vira PDF (documento deslizável no LinkedIn).</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {([
                ['card', 'Card (imagem)'],
                ['carrossel', 'Carrossel (PDF)'],
                ['video', 'Vídeo de slides (MP4)'],
              ] as const).map(([format, text]) => (
                <Button
                  key={format}
                  variant="secondary"
                  disabled={act.busy || (format === 'video' && generation.designVideo === false)}
                  title={format === 'video' && generation.designVideo === false ? 'Servidor sem ffmpeg.' : undefined}
                  onClick={() => void act.run(() => api(`/api/assets/${assetId}/media/design`, { method: 'POST', body: { format } }), 'O Claude está criando; o arquivo aparece aqui em instantes.').then(onChange)}
                >
                  {text}
                </Button>
              ))}
            </div>

            <h3 className="mt-4 text-xs font-semibold uppercase text-slate-500">Gerar imagem/vídeo com IA</h3>
            <div className="mt-1 space-y-2">
              <Field label="Prompt (em inglês, sem texto e sem logos)">
                <textarea className={`${inputCls} font-mono text-xs`} rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
              </Field>
              <div className="flex flex-wrap items-center gap-2">
                <select aria-label="Gerador" className="rounded border border-slate-300 px-2 py-2 text-sm" value={sel.provider} onChange={(e) => setProvider(e.target.value)}>
                  {providers.map((p) => (
                    <option key={p.provider} value={p.provider}>
                      {PROVIDER_LABEL[p.provider] ?? p.provider}{p.status !== 'CONNECTED' ? ' (não configurado)' : ''}
                    </option>
                  ))}
                </select>
                <select aria-label="Tipo de mídia" className="rounded border border-slate-300 px-2 py-2 text-sm" value={kind} onChange={(e) => setKind(e.target.value as 'image' | 'video')}>
                  <option value="image">Imagem</option>
                  <option value="video">Vídeo</option>
                </select>
                <Button disabled={act.busy || !!genReason || prompt.trim().length < 15} title={genReason} onClick={() => void act.run(() => api(`/api/assets/${assetId}/media/generate`, { method: 'POST', body: { kind, prompt, provider: sel.provider } }), 'Geração enviada; o arquivo aparece aqui quando ficar pronto.').then(onChange)}>
                  Gerar no {label}
                </Button>
                {genReason && <span className="text-xs text-slate-600">{genReason}</span>}
              </div>
            </div>

            <h3 className="mt-4 text-xs font-semibold uppercase text-slate-500">Anexar arquivo (designer, Canva, Artlist)</h3>
            <div className="mt-1 grid gap-2 md:grid-cols-2">
              <Field label="Arquivo (PNG, JPG, WEBP, GIF ou MP4 até 100 MB)">
                <input className="text-sm" type="file" accept="image/png,image/jpeg,image/webp,image/gif,video/mp4" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </Field>
              <Field label="Origem">
                <select className={inputCls} value={source} onChange={(e) => setSource(e.target.value)}>
                  <option value="upload">Designer / arquivo próprio</option>
                  <option value="canva">Exportado do Canva</option>
                  <option value="artlist">Gerado na Artlist</option>
                </select>
              </Field>
              <Field label="Direitos de uso" hint="Ex.: gerado na Artlist (plano AI Suite), foto própria com autorização.">
                <input className={inputCls} value={rights} onChange={(e) => setRights(e.target.value)} />
              </Field>
              <label className="flex items-start gap-2 text-sm md:col-span-2">
                <input type="checkbox" className="mt-1" checked={hasLogo} onChange={(e) => setHasLogo(e.target.checked)} />
                <span>Esta arte já tem o logo da 3S (ex.: montada no Canva). Desmarcado, o sistema aplica o logo. GIF e vídeo só são aceitos para aprovação com esta marcação.</span>
              </label>
              <div className="flex items-end">
                <Button variant="secondary" disabled={act.busy || !file} onClick={() => void act.run(upload, 'Arquivo verificado e adicionado à biblioteca da peça.').then(onChange)}>
                  Enviar arquivo
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </>
  );
}
